import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, XCircle, TrendingUp, TrendingDown, ArrowRight, Flame } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const RecentWins = () => {
  const [streakData, setStreakData] = useState({ type: null, count: 0, picks: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAndCalculateStreak = async () => {
      try {
        const response = await axios.get(`${API}/picks`);
        
        // Get picks with outcomes, sorted by date (most recent first)
        const picksWithOutcomes = response.data
          .filter(pick => pick.outcome && pick.outcome !== 'push') // Only wins and losses count for streaks
          .sort((a, b) => new Date(b.pick_date) - new Date(a.pick_date));
        
        if (picksWithOutcomes.length === 0) {
          setStreakData({ type: null, count: 0, picks: [] });
          setLoading(false);
          return;
        }
        
        // Calculate current streak - consecutive same outcomes from most recent
        const firstOutcome = picksWithOutcomes[0].outcome;
        let streakCount = 0;
        let streakPicks = [];
        
        for (const pick of picksWithOutcomes) {
          if (pick.outcome === firstOutcome) {
            streakCount++;
            streakPicks.push(pick);
            if (streakPicks.length >= 5) break; // Show max 5 in the streak
          } else {
            break; // Streak is broken
          }
        }
        
        setStreakData({
          type: firstOutcome, // 'win' or 'loss'
          count: streakCount,
          picks: streakPicks
        });
      } catch (error) {
        console.error('Failed to fetch picks:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchAndCalculateStreak();
  }, []);

  if (loading) {
    return (
      <div className="bg-card border border-border p-6 animate-pulse">
        <div className="h-6 bg-surface-highlight rounded w-48 mb-4"></div>
        <div className="space-y-3">
          <div className="h-10 bg-surface-highlight rounded"></div>
          <div className="h-10 bg-surface-highlight rounded"></div>
          <div className="h-10 bg-surface-highlight rounded"></div>
        </div>
      </div>
    );
  }

  if (streakData.count === 0) {
    return null; // Don't show section if no streak
  }

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const isWinStreak = streakData.type === 'win';

  return (
    <div className="bg-card border border-border p-6" data-testid="recent-streak">
      <div className="flex items-center gap-2 mb-4">
        {isWinStreak ? (
          <TrendingUp className="text-accent" size={20} />
        ) : (
          <TrendingDown className="text-loss" size={20} />
        )}
        <h3 className="font-heading text-xl font-bold">
          {isWinStreak ? 'WINNING STREAK' : 'CURRENT STREAK'}
        </h3>
        <span className={`ml-auto px-2 py-1 rounded font-mono text-xs ${
          isWinStreak 
            ? 'bg-accent/20 text-accent' 
            : 'bg-loss/20 text-loss'
        }`}>
          {isWinStreak ? '🔥' : '📉'} {streakData.count} {isWinStreak ? 'WINS' : 'LOSSES'}
        </span>
      </div>
      
      <div className="space-y-3">
        {streakData.picks.map((pick) => (
          <div 
            key={pick.id} 
            className={`flex items-center gap-3 bg-surface p-3 rounded border-l-4 ${
              isWinStreak ? 'border-accent' : 'border-loss'
            }`}
            data-testid={`streak-pick-${pick.id}`}
          >
            {isWinStreak ? (
              <CheckCircle className="text-accent flex-shrink-0" size={18} />
            ) : (
              <XCircle className="text-loss flex-shrink-0" size={18} />
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono text-xs text-muted-foreground">{formatDate(pick.pick_date)}</span>
                <span className="font-bold truncate">{pick.player_name}</span>
                <span className="text-muted-foreground">-</span>
                <span className={`font-mono text-sm ${isWinStreak ? 'text-primary' : 'text-loss'}`}>
                  {pick.prop_type} {pick.pick}
                </span>
              </div>
              {pick.result && (
                <div className={`text-xs font-mono mt-1 ${isWinStreak ? 'text-accent' : 'text-loss'}`}>
                  {isWinStreak ? '✓' : '✗'} {pick.result}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      <Link 
        to="/results" 
        className="flex items-center justify-center gap-2 mt-4 pt-4 border-t border-border text-primary hover:underline font-mono text-sm uppercase tracking-wider"
        data-testid="see-full-results"
      >
        See Full Results
        <ArrowRight size={14} />
      </Link>
    </div>
  );
};
